import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * The kit's icon drawer.
 *
 * The app draws its icons with FontAwesome, but this package takes react,
 * react-dom and @floating-ui/dom and nothing else (DESIGN.md), so a kit
 * component can never import one. The fonts set the precedent: what the kit
 * needs, the kit carries. Each glyph below is vendored the same way - the path
 * data copied out of @fortawesome/pro-solid-svg-icons@6.7.2 and inlined as a
 * React component, so a kit component draws it with no dependency at all.
 *
 * These are FontAwesome Pro-licensed glyphs, redistributed only inside this
 * private internal package under the org's Pro licence. Nothing here may ship
 * to a public package.
 *
 * The drawer is deliberately closed and small: only glyphs a kit component
 * actually draws are in it. It is not a general icon library, and the app keeps
 * using FontAwesome directly for everything else.
 *
 * Note the two idioms living side by side. The kit's own chrome marks (the
 * Checkbox tick, the Select chevron, the Sheet close) are hand-drawn 16-unit
 * stroke paths, because they are pen strokes on paper, not glyphs. These are
 * filled glyphs at FontAwesome's own scale. Do not redraw one as the other -
 * add to this file only when a kit component needs a glyph the app already
 * spells with FontAwesome, so the two render identically side by side.
 */

/*
 * Solid path data, verbatim from @fortawesome/pro-solid-svg-icons@6.7.2. Each
 * viewBox is that icon's own (FontAwesome glyphs are 512 tall and vary in
 * width), which is what keeps every glyph's optical weight matched.
 */
const TRIANGLE_EXCLAMATION =
  "M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z";

const EYE =
  "M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z";

const CIRCLE_INFO =
  "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z";

const NOTE_STICKY =
  "M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l224 0 0-112c0-26.5 21.5-48 48-48l112 0 0-224c0-35.3-28.7-64-64-64L64 32zM448 352l-45.3 0L336 352c-8.8 0-16 7.2-16 16l0 66.7 0 45.3 32-32 64-64 32-32z";

const MICROPHONE =
  "M192 0C139 0 96 43 96 96l0 160c0 53 43 96 96 96s96-43 96-96l0-160c0-53-43-96-96-96zM64 216c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40c0 89.1 66.2 162.7 152 174.4l0 33.6-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l72 0 72 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-48 0 0-33.6c85.8-11.7 152-85.3 152-174.4l0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40c0 70.7-57.3 128-128 128s-128-57.3-128-128l0-40z";

const LIST_CHECK =
  "M152.1 38.2c9.9 8.9 10.7 24 1.8 33.9l-72 80c-4.4 4.9-10.6 7.8-17.2 7.9s-12.9-2.4-17.6-7L7 113C-2.3 103.6-2.3 88.4 7 79s24.6-9.4 33.9 0l22.1 22.1 55.1-61.2c8.9-9.9 24-10.7 33.9-1.8zm0 160c9.9 8.9 10.7 24 1.8 33.9l-72 80c-4.4 4.9-10.6 7.8-17.2 7.9s-12.9-2.4-17.6-7L7 273c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l22.1 22.1 55.1-61.2c8.9-9.9 24-10.7 33.9-1.8zM224 96c0-17.7 14.3-32 32-32l224 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-224 0c-17.7 0-32-14.3-32-32zm0 160c0-17.7 14.3-32 32-32l224 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-224 0c-17.7 0-32-14.3-32-32zM160 416c0-17.7 14.3-32 32-32l288 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-288 0c-17.7 0-32-14.3-32-32zM48 368a48 48 0 1 1 0 96 48 48 0 1 1 0-96z";

const BOLT =
  "M349.4 44.6c5.9-13.7 1.5-29.7-10.6-38.5s-28.6-8-39.9 1.8l-256 224c-10 8.8-13.6 22.9-8.9 35.3S50.7 288 64 288l111.5 0L98.6 467.4c-5.9 13.7-1.5 29.7 10.6 38.5s28.6 8 39.9-1.8l256-224c10-8.8 13.6-22.9 8.9-35.3s-16.6-20.7-30-20.7l-111.5 0L349.4 44.6z";

/*
 * One glyph, sized and coloured like a letter: `1em` tall so it tracks whatever
 * type it sits in, width from its own aspect ratio, `currentColor` so it takes
 * the ink of the text around it, and FontAwesome's -0.125em baseline nudge so
 * it sits on the line the way the app's icons already do. Callers change size
 * by changing the font size, or by overriding the height in `className`.
 */
const GLYPH_SHAPE =
  "inline-block h-[1em] w-auto shrink-0 fill-current align-[-0.125em]";

export interface IconProps extends Omit<
  React.SVGAttributes<SVGSVGElement>,
  "children" | "viewBox"
> {
  /** Merged onto the glyph's own sizing classes. */
  className?: string;
  ref?: React.Ref<SVGSVGElement>;
}

interface GlyphProps extends IconProps {
  viewBox: string;
  d: string;
}

/**
 * Internal renderer shared by every glyph in the drawer.
 *
 * Hidden from assistive tech by default. That is the wash rule's philosophy
 * applied to icons: a glyph never carries the meaning on its own, there is
 * always a word beside it, and announcing both just says it twice. Pass
 * `aria-label` (or `aria-labelledby`) for the rare wordless case and the icon
 * becomes a labelled `img` instead.
 */
function Glyph({ viewBox, d, className, ...rest }: GlyphProps) {
  const labelled =
    rest["aria-label"] != null || rest["aria-labelledby"] != null;

  return (
    <svg
      viewBox={viewBox}
      focusable="false"
      role={labelled ? "img" : undefined}
      aria-hidden={labelled ? undefined : true}
      {...rest}
      className={cn(GLYPH_SHAPE, className)}
    >
      <path d={d} />
    </svg>
  );
}

/** Warning triangle: the glyph on a failure, in `ink-stopped`. */
export function TriangleExclamationIcon(props: IconProps) {
  return <Glyph viewBox="0 0 512 512" d={TRIANGLE_EXCLAMATION} {...props} />;
}

/** Open eye: something to keep an eye on, the `flag` half of a severity mark. */
export function EyeIcon(props: IconProps) {
  return <Glyph viewBox="0 0 576 512" d={EYE} {...props} />;
}

/** Circled "i": a recorded fact, the calmest thing on a timeline. */
export function CircleInfoIcon(props: IconProps) {
  return <Glyph viewBox="0 0 512 512" d={CIRCLE_INFO} {...props} />;
}

/** Dog-eared square: a memo, the same object the `StickyNote` draws in full. */
export function NoteStickyIcon(props: IconProps) {
  return <Glyph viewBox="0 0 448 512" d={NOTE_STICKY} {...props} />;
}

/** Microphone: a transcript, something that was said out loud. */
export function MicrophoneIcon(props: IconProps) {
  return <Glyph viewBox="0 0 384 512" d={MICROPHONE} {...props} />;
}

/** Checked list: a task, work with a box beside it. */
export function ListCheckIcon(props: IconProps) {
  return <Glyph viewBox="0 0 512 512" d={LIST_CHECK} {...props} />;
}

/** Lightning bolt: an event, something that happened on its own. */
export function BoltIcon(props: IconProps) {
  return <Glyph viewBox="0 0 448 512" d={BOLT} {...props} />;
}
